import { subHours, addHours, format, formatISO } from "date-fns";
import sampleSize from "lodash/sampleSize";
import { IItem, IItemProperty } from "../interfaces/IItem";
import { PropertyDataType } from "../constants/datatypes";
import { determineRootObjectTypes } from "../helpers/configurationHelpers";
import testProperties from "./properties.json";
import testPropertyDefinitions from "./propertyDefinitions.json";
import { IConfiguration, IViewConfigurationItem } from "../interfaces/configuration/IConfiguration";
import { ViewType } from "../constants/viewTypes";
import IMFilesPropertyDefinition from "../interfaces/mfiles/IMFilesPropertyDefinition";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getTestData = (targetDate: Date, configuration: IConfiguration) => {
  const items = getRandomDataSet(targetDate, configuration);
  return {
    items: items,
    moreResults: [true, false][getRandomInt(0, 1)],
  };
};

// Get random data for views mimicing the actual configuration
const getRandomDataSet = (targetDate: Date, configuration: IConfiguration): Array<IItem> => {
  const allData = [];
  let index = 0;

  for (let i = 0; i < configuration.viewConfigurations.length; i++) {
    const viewConfig = configuration.viewConfigurations[i];
    const rootObjectTypeIds = determineRootObjectTypes(viewConfig);

    // Create root level objects
    const rootItems: Array<IItem> = [];
    for (const objectTypeId of rootObjectTypeIds) {
      const objectTypeConfig = configuration.objectTypeConfigurations.get(objectTypeId);
      const amount = getRandomInt(10, 50);
      for (let k = 0; k < amount; k++) {
        const name = viewConfig.viewType === ViewType.Resourcing ? getRandomPersonName() : getRandomTaskName();
        const item = createItem(index, objectTypeConfig.objectTypeId, targetDate, getRandomProperties(testProperties), name);
        rootItems.push(item);
        index += 1;
      }
    }
    allData.push(...rootItems);

    for (let l = 0; l < rootItems.length; l++) {
      index = createChildItemsRecursively(rootItems[l], viewConfig, allData, index, targetDate);
    }
  }
  return allData;
};

const createChildItemsRecursively = (
  parentItem: IItem,
  viewConfig: IViewConfigurationItem,
  allData: Array<IItem>,
  index: number,
  targetDate: Date
): number => {
  const childItems = [];
  const childConfigurations = viewConfig.objectHierarchy.get(parentItem.objectType).childObjectTypes;
  for (let j = 0; j < childConfigurations.length; j++) {
    const [objectType, parentPropertyId] = childConfigurations[j];
    const amount = getRandomInt(0, 4);
    for (let k = 0; k < amount; k++) {
      let item = createItem(index, objectType, targetDate, getRandomProperties(testProperties), getRandomTaskName());
      item = upsertParentProperty(item, parentPropertyId, parentItem);
      childItems.push(item);
      index += 1;
    }
  }
  allData.push(...childItems);

  for (let k = 0; k < childItems.length; k++) {
    index = createChildItemsRecursively(childItems[k], viewConfig, allData, index, targetDate);
  }

  return index;
};

const upsertParentProperty = (item: IItem, parentPropertyId: number, parent: IItem): IItem => {
  const parentProperty: IItemProperty = {
    dataType: PropertyDataType.Lookup,
    value: parent.objectId.toString(),
    displayValue: parent.name,
    property: parentPropertyId,
  };

  return {
    ...item,
    properties: [...item.properties.filter((p) => p.property !== parentPropertyId), parentProperty],
  };
};

const createItem = (index, objectType, targetDate, properties, name): IItem => {
  const { startDate, endDate } = getRandomStartEnd(targetDate);
  const color = getRandomColor();
  const item: IItem = {
    id: index,
    objectId: index,
    objectType: objectType,
    objectVersion: index,
    properties: properties,
    start: startDate,
    end: endDate,
    color: color,
    name: name,
    linksTo: [],
    linksFrom: [],
    isCheckedOut: false,
    isCheckedOutToThisUser: false,
    status: 0,
  };
  return item;
};

const getRandomProperties = (allProperties: Array<IItemProperty>): Array<IItemProperty> => {
  const subsetOfPropertyDefs: Array<IItemProperty> = sampleSize(allProperties, getRandomInt(1, Math.floor(allProperties.length * 0.9)));
  const properties: Array<IItemProperty> = subsetOfPropertyDefs.map((prop) => {
    const { value, displayValue } = getRandomValueForDataType(prop.dataType);
    return {
      dataType: prop.dataType,
      value: value,
      displayValue: displayValue,
      property: prop.property,
    };
  });
  return properties;
};

export const getPropertyDefinitionsMapping = (): Map<number, IMFilesPropertyDefinition> => {
  return new Map(
    testPropertyDefinitions.map((propDefJson) => {
      const propDef = propDefJson as IMFilesPropertyDefinition;
      return [propDef.ID, propDef];
    })
  );
};

const getRandomValueForDataType = (dataType: PropertyDataType): { value: string | null; displayValue: string } => {
  let value: string = null;
  let displayValue = "";

  switch (dataType) {
    case PropertyDataType.Boolean:
      value = ["true", "false", ""][getRandomInt(0, 2)];
      displayValue = value;
      break;
    case PropertyDataType.Date:
      value = formatISO(getRandomNearbyDate(new Date()));
      displayValue = value;
      break;
    case PropertyDataType.Floating:
      value = (getRandomInt(1, 100) * 1.1).toString();
      displayValue = value;
      break;
    case PropertyDataType.Integer:
      value = getRandomInt(1, 100).toString();
      displayValue = value;
      break;
    case PropertyDataType.MultilineText:
      value = "Some multiline text\nText\n";
      displayValue = value;
      break;
    case PropertyDataType.Text:
      value = "Some text";
      displayValue = value;
      break;
    case PropertyDataType.Time:
      value = format(new Date(), "HH:mm:ss");
      displayValue = value;
      break;
    case PropertyDataType.MultiSelectLookup:
    case PropertyDataType.Lookup:
      value = getRandomInt(1, 100).toString();
      displayValue = getRandomPersonName();
      break;
    default:
      break;
  }

  return { value, displayValue };
};

const getRandomNearbyDate = (targetDate: Date) => subHours(targetDate, getRandomInt(-700, 700));

const getRandomStartEnd = (targetDate: Date) => {
  const startDate = getRandomNearbyDate(targetDate);
  const duration = getRandomInt(1, 400);
  const endDate = addHours(startDate, duration);
  return { startDate, endDate };
};

const getRandomInt = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

const getRandomPersonName = () => {
  const forenames = [
    "Kyllikki",
    "Jorma",
    "Pate",
    "Pertsa",
    "Simón",
    "Skärbä",
    "Lintsu",
    "Tillu",
    "Padde",
    "Monde",
    "Sile",
    "Rena",
    "Allu",
    "Kata",
    "Bono",
    "Solo",
    "Jyty",
    "Leka",
    "Heke",
    "Ellu",
    "Tsärbä",
    "Penu",
    "Holo",
    "Epe",
    "Vasa",
    "Pihla",
    "Emilia",
    "Isla",
    "Mila",
    "Minea",
    "Hilla",
    "Ellen",
    "Aava",
    "Elsa",
    "Kerttu",
    "Linnea",
    "Vilma",
    "Alisa",
    "Siiri",
    "Iida",
    "Amanda",
    "Viola",
    "Anni",
    "Viivi",
    "Alma",
    "Oona",
    "Ilona",
    "Nella",
    "Alina",
    "Iina",
    "Seela",
    "Lumi",
    "Matilda",
    "Sara",
    "Hilda",
  ];
  const surnames = [
    "Ahmakangas",
    "Ahmalahti",
    "Ahmalampi",
    "Ahmaneva,",
    "Yläpuro",
    "Yläraivio",
    "Yrttijärvi",
    "Varpuvaara",
    "Varsakallio",
    "Varsakorpi",
    "Varsalahti",
    "Varsalampi",
    "Tuppilahti",
    "Tuppilampi",
    "Tuppimäki",
    "Tuppiniemi",
    "Turkkilampi",
    "Turkkisaari",
    "Selkälampi",
    "Selkäluoto",
    "Selkämä",
    "Selkämäki",
    "Selkäneva",
    "Purulahti",
    "Purumäki",
    "Puruniemi",
    "Puruoja",
    "Putaanlahti",
    "Putaanniemi",
    "Pajapelto",
    "Pajarila",
    "Pajasaari",
    "Kunttisuo",
    "Kuohuaho",
    "Kuohukangas",
    "Kuohulampi",
    "Kuohumäki",
    "Kuohupuro",
    "Kuohusuo",
    "Kuohuvaara",
    "Jänisluoto",
    "Jänismaa",
    "Jänisneva",
    "Jänispuro",
    "Jänissaari",
    "Jänissalmi",
    "Jänissalo",
    "Jänisselkä",
  ];
  const pickForename = Math.floor(Math.random() * forenames.length);
  const pickSurname = Math.floor(Math.random() * surnames.length);
  return `${forenames[pickForename]} ${surnames[pickSurname]}`;
};

const getRandomTaskName = () => {
  const allNames = [
    "Fiilataan ja höylätään",
    "Apinaa koijataan",
    "Лорем ипсум долор сит амет",
    "102901.00 *Twenty-Third maintenance plan task temlate",
    "1364  111 Vincit  12121",
    "257 Piipun huolto Buffer 3 (Unit Month)",
    "256 MP MP",
    "254 Piipun huolto Buffer 1",
    "1369 - Nro 1 - MP001 Carton box 50x50x50 - 2 kpl",
    "1385 000070 Verkkokauppa.com Oyj COMPUTER-GADGET",
    "1378 1234001 Kolmeks LVI-tuotteet Oy",
    "010101 TEST THE WHOLE PROCEDURE",
    "1357 - Nro 1 - 000010 SKU SITE MOBILE (W) - 1 pcs",
    "1372 000000 THE FIRST SUPPLIER FOR SITE 99",
    "NOK - Nokia warehouse NOK-1 Nokia WH1 MA54321 Maalipensseli",
    "1357  1234001 Kolmeks LVI-tuotteet Oy",
    "Proposal - 1363",
    "1391 CFR Jokilaakeri 12345",
    "1369  CFR Jokilaakeri  12345",
    "[354 333 1370 000070 Verkkokauppa.com Oyj.1] MP001 Carton box 50x50x50 - 1,00 - kpl",
    "Adorjani Hunor",
    "102906 Tutka ei pyöri",
    "Cvetkovic Pavle",
    "1385 - Nro 1 - MP001 Carton box 50x50x50 - 1 kpl",
    "000000 - MAIN SITE 99 000000 MAIN - LEFT SIDE 000010 SKU SITE MOBILE (W)",
    "1378 - Nro 1 - 999999 Kertatilausnimike (category) - 1 pcs",
    "[3658] New Henrik Test 4.3.2020",
    "1372 - Nro 1 - 000004 SKU SITE MOBILE (U) - 15 pcs",
    "1381 - Nro 5 - 000012 SKU SITE MOBILE (W) - 1 pcs",
    "1391 - Nro 1 - MA54321 Maalipensseli - 10 kpl",
    "1389 - Nro 1 - 000077 DELL P2418D 23.8, IPS, 2560 x 1440 WQHD, 8ms - 2 pcs",
    "1389 000070 Verkkokauppa.com Oyj COMPUTER-GADGET",
    "000077 DELL P2418D 23.8, IPS, 2560 x 1440 WQHD, 8ms",
    "1367 - Nro 3 - 000000 SKU SITE MOBILE (U) - 7 pcs",
    "1367 - Nro 1 - 000001 SKU SITE MOBILE (U) - 1 pcs",
    "1365 - Nro 1 - MP001 Carton box 50x50x50 - 10 kpl",
  ];
  const pick = Math.floor(Math.random() * allNames.length);
  return allNames[pick];
};

export const getRandomAliasMapping = (aliases: Array<string>): Map<string, number> => {
  return new Map(aliases.map((alias, i) => [alias, i]));
};

export const getPropertyDefinitions = (): IMFilesPropertyDefinition[] => testPropertyDefinitions;
